import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@tkpod/ui';
import { optionsBlockConfigSchema, type OptionsBlockConfig } from '@tkpod/shared';
import { ApiClientError } from '@tkpod/api-client';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

const SOURCES = ['DEVICE', 'RECENT', 'PHONE', 'AI'] as const;

export function OptionsBlockConfigCard({
  templateId,
  unitCents = null,
  onDirtyChange,
}: {
  templateId: string;
  /** Portal unit in cents for the priced dimension, when one is set. */
  unitCents?: number | null;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['product-options-block', templateId],
    queryFn: () =>
      api.get<OptionsBlockConfig>(
        `/api/v1/admin/catalog/templates/${templateId}/options-block-config`,
      ),
  });
  const [draft, setDraft] = useState<OptionsBlockConfig | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!hydrated && query.data) {
      const sources = query.data.uploadPanel.sources.filter((source) => source !== 'GALLERY');
      setDraft({
        ...query.data,
        uploadPanel: { sources: sources.length > 0 ? sources : ['DEVICE'] },
      });
      setHydrated(true);
    }
  }, [hydrated, query.data]);

  const dirty = draft != null && query.data != null && JSON.stringify(draft) !== JSON.stringify(query.data);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const save = useMutation({
    mutationFn: (body: OptionsBlockConfig) =>
      api.patch<OptionsBlockConfig>(
        `/api/v1/admin/catalog/templates/${templateId}/options-block-config`,
        body,
      ),
    onSuccess: (saved) => {
      setDraft(saved);
      setFieldErrors(new Map());
      queryClient.setQueryData(['product-options-block', templateId], saved);
      toast('Options block saved.', 'success');
    },
    onError: (err: Error) => {
      const details = err instanceof ApiClientError ? err.apiError?.details ?? [] : [];
      setFieldErrors(new Map(details.map((detail) => [detail.field, detail.message])));
      const detailText = details.map((detail) => detail.message).filter(Boolean).join(' ');
      toast(detailText || err.message || 'Could not save the options block.', 'error');
    },
  });

  if (!draft) {
    return (
      <Card>
        <p className="px-6 py-4 text-sm text-text-secondary">Loading block settings…</p>
      </Card>
    );
  }

  const toggleSource = (source: (typeof SOURCES)[number]) => {
    const current = draft.uploadPanel.sources;
    const next = current.includes(source)
      ? current.filter((item) => item !== source)
      : [...current, source];
    if (next.length === 0) return;
    setDraft({ ...draft, uploadPanel: { sources: next } });
  };

  const submit = () => {
    const parsed = optionsBlockConfigSchema.safeParse(draft);
    if (!parsed.success) {
      const issues = new Map(
        parsed.error.issues.map((issue) => [issue.path.map(String).join('.'), issue.message]),
      );
      setFieldErrors(issues);
      toast(parsed.error.issues[0]?.message ?? 'Check the block settings.', 'error');
      return;
    }
    setFieldErrors(new Map());
    save.mutate(parsed.data);
  };

  return (
    <Card>
      <div className="space-y-4 px-6 py-4">
        <h3 className="text-sm font-semibold text-text-primary">Storefront block</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <NumberField
            label="Quantity min"
            value={draft.quantity.min}
            error={fieldErrors.get('quantity.min')}
            onChange={(min) => setDraft({ ...draft, quantity: { ...draft.quantity, min } })}
          />
          <NumberField
            label="Quantity max"
            value={draft.quantity.max}
            error={fieldErrors.get('quantity.max')}
            onChange={(max) => setDraft({ ...draft, quantity: { ...draft.quantity, max } })}
          />
          <NumberField
            label="Quantity step"
            value={draft.quantity.step}
            error={fieldErrors.get('quantity.step')}
            onChange={(step) => setDraft({ ...draft, quantity: { ...draft.quantity, step } })}
          />
        </div>
        <NumberField
          label="Minimum charge per piece (cents)"
          value={draft.minChargeCents}
          error={fieldErrors.get('minChargeCents')}
          onChange={(minChargeCents) => setDraft({ ...draft, minChargeCents })}
        />
        <p className="text-xs text-text-secondary">
          The smallest price for one piece. A size that prices below this is raised to it.
          Whole cents, so 250 is $2.50. 0 means no minimum.
        </p>
        <fieldset className="space-y-2">
          <legend className="text-sm text-text-secondary">Quantity mode</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="quantity-mode"
              checked={draft.quantity.mode !== 'BULK'}
              onChange={() => setDraft({ ...draft, quantity: { ...draft.quantity, mode: 'SINGLE', tiers: [] } })}
            />
            Single
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="quantity-mode"
              checked={draft.quantity.mode === 'BULK'}
              onChange={() =>
                setDraft({
                  ...draft,
                  quantity: {
                    ...draft.quantity,
                    mode: 'BULK',
                    tiers: draft.quantity.tiers.length > 0
                      ? draft.quantity.tiers
                      : [{ minQty: draft.quantity.min, discountPercent: 0 }],
                  },
                })
              }
            />
            Bulk
          </label>
          {draft.quantity.mode === 'BULK' &&
            draft.quantity.tiers.map((tier, index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <NumberField
                  label="From quantity"
                  value={tier.minQty}
                  error={fieldErrors.get(`quantity.tiers.${index}.minQty`)}
                  onChange={(minQty) => {
                    const tiers = draft.quantity.tiers.map((row, rowIndex) =>
                      rowIndex === index ? { ...row, minQty } : row,
                    );
                    setDraft({ ...draft, quantity: { ...draft.quantity, tiers } });
                  }}
                />
                <NumberField
                  label="Discount %"
                  value={tier.discountPercent}
                  error={fieldErrors.get(`quantity.tiers.${index}.discountPercent`)}
                  onChange={(discountPercent) => {
                    const tiers = draft.quantity.tiers.map((row, rowIndex) =>
                      rowIndex === index ? { ...row, discountPercent } : row,
                    );
                    setDraft({ ...draft, quantity: { ...draft.quantity, tiers } });
                  }}
                />
                <button
                  type="button"
                  className="self-end text-xs text-danger-700"
                  onClick={() => {
                    const tiers = draft.quantity.tiers.filter((_, rowIndex) => rowIndex !== index);
                    setDraft({ ...draft, quantity: { ...draft.quantity, tiers } });
                  }}
                >
                  Remove
                </button>
                <p className="sm:col-span-3 text-xs text-text-secondary">
                  {tierMargin(tier.discountPercent, unitCents)}
                </p>
              </div>
            ))}
          {draft.quantity.mode === 'BULK' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setDraft({
                  ...draft,
                  quantity: {
                    ...draft.quantity,
                    tiers: [...draft.quantity.tiers, { minQty: draft.quantity.max, discountPercent: 0 }],
                  },
                })
              }
            >
              Add tier
            </Button>
          )}
          {fieldErrors.get('quantity.tiers') ? (
            <p className="text-xs text-danger-700">{fieldErrors.get('quantity.tiers')}</p>
          ) : null}
        </fieldset>
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={draft.hideNativeSelectors}
            onChange={(event) => setDraft({ ...draft, hideNativeSelectors: event.target.checked })}
          />
          Hide the theme's native variant selectors
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">CSS selectors to hide</span>
          <textarea
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            rows={2}
            value={draft.nativeSelectorCss}
            onChange={(event) => setDraft({ ...draft, nativeSelectorCss: event.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={draft.showPrice}
            onChange={(event) => setDraft({ ...draft, showPrice: event.target.checked })}
          />
          Show price
        </label>
        <label className="block text-sm">
          <span className="text-text-secondary">Add to cart label</span>
          <input
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            value={draft.addToCartLabel}
            onChange={(event) => setDraft({ ...draft, addToCartLabel: event.target.value })}
          />
          {fieldErrors.get('addToCartLabel') ? (
            <p className="mt-1 text-xs text-danger-700">{fieldErrors.get('addToCartLabel')}</p>
          ) : null}
        </label>
        <fieldset>
          <legend className="text-sm text-text-secondary">Upload panel sources</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {SOURCES.map((source) => (
              <label key={source} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.uploadPanel.sources.includes(source)}
                  onChange={() => toggleSource(source)}
                />
                {source}
              </label>
            ))}
          </div>
        </fieldset>
        <Button size="sm" loading={save.isPending} onClick={submit}>
          Save block
        </Button>
      </div>
    </Card>
  );
}

function tierMargin(discountPercent: number, unitCents: number | null): string {
  const payPercent = Math.max(0, 100 - discountPercent);
  if (unitCents == null || unitCents <= 0) {
    return `Customer pays ${payPercent}% of the unit. Catalog cost is not discounted.`;
  }
  const payCents = Math.round((unitCents * payPercent) / 100);
  const unit = dollars(unitCents);
  return `Customer pays ${dollars(payCents)} of a ${unit} unit. Catalog cost stays ${unit}.`;
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function NumberField({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: number;
  error?: string;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <label className="block text-sm">
      <span className="text-text-secondary">{label}</span>
      <input
        type="number"
        className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
        value={text ?? String(value)}
        onChange={(event) => {
          setText(event.target.value);
          if (event.target.value.trim() === '') return;
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        onBlur={() => setText(null)}
      />
      {error ? <p className="mt-1 text-xs text-danger-700">{error}</p> : null}
    </label>
  );
}
